import EmblaCarousel from './embla-carousel.esm.js';

class EmblaSliderComponent extends HTMLElement {
  constructor() {
    super();

    this.embla = null;
    this.autoplayTimer = null;
    this.root = null;
    this.viewport = null;
    this.controls = null;
    this.dotsWrapper = null;
    this.prevButton = null;
    this.nextButton = null;
    this.progressBar = null;
    this.loadmore = null;
    this.slides = [];
    this.loadedCounts = null;
    this.isWheelScrolling = false

    this.onResize = this.onResize.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
    this.onMouseEnter = this.onMouseEnter.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);
    this.onFocusIn = this.onFocusIn.bind(this);
    this.onFocusOut = this.onFocusOut.bind(this);
    this.onPrevClick = this.onPrevClick.bind(this);
    this.onNextClick = this.onNextClick.bind(this);
    this.onDotsClick = this.onDotsClick.bind(this);

    this.onEmblaSelect = this.onEmblaSelect.bind(this);
    this.onEmblaReInit = this.onEmblaReInit.bind(this);
    this.onEmblaScroll = this.onEmblaScroll.bind(this);
    this.onLoadMore = this.onLoadMore.bind(this);
    this.setLoadMore = this.setLoadMore.bind(this);
  }

  connectedCallback() {
    this.setup();
  }

  disconnectedCallback() {
    this.destroy();
  }

  setup() {
    this.destroy();

    this.root = this.closest('.embla-slider');
    this.viewport = this.querySelector('[data-embla-viewport]');

    if (!this.root || !this.viewport) {
      return;
    }

    this.slides = Array.from(this.querySelectorAll('.embla-slider__slide'));
    this.labelSlides();
    this.controls = this.querySelector('[data-embla-controls]');
    this.dotsWrapper = this.querySelector('[data-embla-dots]');
    this.prevButton = this.querySelector('[data-embla-prev]');
    this.nextButton = this.querySelector('[data-embla-next]');
    this.progressBar = this.querySelector('[data-embla-progress-bar]');
    this.loadmore = this.querySelector('.loadmore');

    this.embla = EmblaCarousel(this.viewport, this.getEmblaOptions());

    this.embla.on('select', this.onEmblaSelect);
    this.embla.on('reInit', this.onEmblaReInit);
    this.embla.on('scroll', this.onEmblaScroll);

    this.prevButton?.addEventListener('click', this.onPrevClick);
    this.nextButton?.addEventListener('click', this.onNextClick);
    this.dotsWrapper?.addEventListener('click', this.onDotsClick);

    this.addEventListener('mouseenter', this.onMouseEnter);
    this.addEventListener('mouseleave', this.onMouseLeave);
    this.addEventListener('focusin', this.onFocusIn);
    this.addEventListener('focusout', this.onFocusOut);

    window.addEventListener('resize', this.onResize);
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    this.onEmblaReInit();
    this.onEmblaSelect();
    this.onEmblaScroll();

    this.resumeAutoplay();

    this.setLoadMore();
    this.loadmore?.addEventListener('click', this.onLoadMore);
  }

  /**
   * Opt-in carousel semantics: when the slider block has a "Screen reader name", its
   * root carries a slide label template and role description (see embla-slider.liquid).
   * Each slide becomes a group announced with its position, e.g. "1 of 6, slide".
   * Slides can't know their own position in Liquid, so it is set here.
   */
  labelSlides() {
    const template = this.root?.dataset.slideLabel;
    if (!template) return;

    const roleDescription = this.root.dataset.slideRoledescription;
    const total = `${this.slides.length}`;

    for (const [index, slide] of this.slides.entries()) {
      slide.setAttribute('role', 'group');
      if (roleDescription) slide.setAttribute('aria-roledescription', roleDescription);
      slide.setAttribute('aria-label', template.replace('[index]', `${index + 1}`).replace('[length]', total));
    }
  }

  destroy() {
    this.pauseAutoplay();

    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener('resize', this.onResize);

    this.removeEventListener('mouseenter', this.onMouseEnter);
    this.removeEventListener('mouseleave', this.onMouseLeave);
    this.removeEventListener('focusin', this.onFocusIn);
    this.removeEventListener('focusout', this.onFocusOut);

    this.prevButton?.removeEventListener('click', this.onPrevClick);
    this.nextButton?.removeEventListener('click', this.onNextClick);
    this.dotsWrapper?.removeEventListener('click', this.onDotsClick);

    this.resetSlidesState();

    if (this.embla) {
      this.embla.destroy();
      this.embla = null;
    }

    this.slides = [];
  }

  getEmblaOptions() {
    const options = {
      loop: this.root?.dataset.loop === 'true',
      breakpoints: {
        '(max-width: 749px)': { active: this.root?.dataset.mobileSliderEnabled === 'true' },
        '(min-width: 750px) and (max-width: 989px)': { active: this.root?.dataset.tabletSliderEnabled === 'true' },
        '(min-width: 990px)': { active: this.root?.dataset.desktopSliderEnabled === 'true' },
      },
    };

    if (this.getTransitionEffect() === 'fade') {
      options.align = 'center';
      options.containScroll = false;
      return options;
    }

    if (this.querySelector(".ingredient-card")) {
      options.align = 'center';
      options.containScroll = false;
      return options;
    }

    options.align = 'start';
    options.containScroll = 'trimSnaps';
    return options;
  }

  getControlsMode() {
    return this.root?.dataset.controlsMode || 'none';
  }

  getSlideCount() {
    return this.querySelectorAll('.embla-slider__slide').length;
  }

  getTransitionEffect() {
    return this.root?.dataset.transitionEffect === 'fade' ? 'fade' : 'slide';
  }

  getAutoplayEnabled() {
    return this.root?.dataset.autoplay === 'true';
  }

  getAutoplayIntervalMs() {
    const seconds = Number(this.root?.dataset.autoplaySpeed || 4);
    if (!Number.isFinite(seconds) || seconds <= 0) {
      return 4000;
    }

    return seconds * 1000;
  }

  isSliderEnabledForViewport() {
    if (window.matchMedia('(min-width: 990px)').matches) {
      return this.root?.dataset.desktopSliderEnabled === 'true';
    }

    if (window.matchMedia('(min-width: 750px)').matches) {
      return this.root?.dataset.tabletSliderEnabled === 'true';
    }

    return this.root?.dataset.mobileSliderEnabled === 'true';
  }

  onEmblaReInit() {
    this.updateFadeModeState();
    this.renderDots();
    this.updateControlsState();
  }

  onEmblaSelect() {
    this.updateSelectedSlideState();
    this.updateControlsState();
    this.updateDotsState();
  }

  onEmblaScroll() {
    this.updateProgressBar();
    this.setScrollSliding();
  }

  onPrevClick() {
    this.embla?.scrollPrev();
  }

  onNextClick() {
    this.embla?.scrollNext();
  }

  onDotsClick(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const dotButton = target.closest('[data-embla-dot]');
    if (!(dotButton instanceof HTMLButtonElement)) return;

    const slideIndex = Number(dotButton.dataset.emblaDot);
    if (Number.isNaN(slideIndex)) return;

    this.embla?.scrollTo(slideIndex);
  }

  onResize() {
    this.updateFadeModeState();
    this.updateControlsState();
    this.updateProgressBar();
    this.resumeAutoplay();
  }

  onMouseEnter() {
    this.pauseAutoplay();
  }

  onMouseLeave() {
    this.resumeAutoplay();
  }

  onFocusIn() {
    this.pauseAutoplay();
  }

  onFocusOut(event) {
    const nextFocusTarget = event.relatedTarget;
    if (!(nextFocusTarget instanceof Node) || !this.contains(nextFocusTarget)) {
      this.resumeAutoplay();
    }
  }

  onVisibilityChange() {
    if (document.hidden) {
      this.pauseAutoplay();
      return;
    }

    this.resumeAutoplay();
  }

  renderDots() {
    if (!this.dotsWrapper || this.getControlsMode() !== 'dots') {
      return;
    }

    if (!this.embla) {
      this.dotsWrapper.replaceChildren();
      return;
    }

    const snapCount = this.embla.scrollSnapList().length;
    const fragment = document.createDocumentFragment();

    for (let index = 0; index < snapCount; index += 1) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'embla-slider__dot';
      button.setAttribute('data-embla-dot', String(index));
      button.setAttribute('aria-label', `Go to slide ${index + 1}`);
      fragment.append(button);
    }

    this.dotsWrapper.replaceChildren(fragment);
    this.updateDotsState();
  }

  updateDotsState() {
    if (!this.dotsWrapper || !this.embla || this.getControlsMode() !== 'dots') {
      return;
    }

    const selected = this.embla.selectedScrollSnap();
    const dots = this.dotsWrapper.querySelectorAll('[data-embla-dot]');

    dots.forEach((dot, index) => {
      if (index === selected) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  updateControlsState() {
    const sliderEnabled = this.isSliderEnabledForViewport();
    const hasMultipleSlides = this.getSlideCount() > 1;

    if (this.controls) {
      this.controls.hidden = !sliderEnabled || !hasMultipleSlides || this.getControlsMode() === 'none';
    }

    if (!this.embla || !sliderEnabled || !hasMultipleSlides) {
      this.prevButton?.setAttribute('disabled', '');
      this.nextButton?.setAttribute('disabled', '');
      return;
    }

    if (this.prevButton) {
      if (this.embla.canScrollPrev()) {
        this.prevButton.removeAttribute('disabled');
      } else {
        this.prevButton.setAttribute('disabled', '');
      }
    }

    if (this.nextButton) {
      if (this.embla.canScrollNext()) {
        this.nextButton.removeAttribute('disabled');
      } else {
        this.nextButton.setAttribute('disabled', '');
      }
    }
  }

  updateFadeModeState() {
    if (!this.root) {
      return;
    }

    const shouldUseFade =
      this.getTransitionEffect() === 'fade' &&
      this.isSliderEnabledForViewport() &&
      this.getSlideCount() > 1;

    this.root.classList.toggle('is-fade-active', shouldUseFade);

    if (!shouldUseFade) {
      this.resetSlidesState();
      return;
    }

    this.updateSelectedSlideState();
  }

  updateSelectedSlideState() {
    // if (!this.embla || !this.root || !this.root.classList.contains('is-fade-active')) {
    //   return;
    // }

    if (!this.embla || !this.root || !this.root.classList.contains('ingredients-slider')) {
      return;
    }

    const selectedSnap = this.embla.selectedScrollSnap();

    this.slides.forEach((slide, index) => {
      const isSelected = index === selectedSnap;
      slide.classList.toggle('is-selected', isSelected);

      if (isSelected) {
        slide.removeAttribute('aria-hidden');
        slide.removeAttribute('inert');
      } else {
        slide.setAttribute('aria-hidden', 'true');
        slide.setAttribute('inert', '');
      }
    });

    this.root.classList.add('has-selected-slide');
  }

  resetSlidesState() {
    if (this.root) {
      this.root.classList.remove('is-fade-active', 'has-selected-slide');
    }

    this.slides.forEach((slide) => {
      slide.classList.remove('is-selected');
      slide.removeAttribute('aria-hidden');
      slide.removeAttribute('inert');
    });
  }

  updateProgressBar() {
    if (!this.progressBar) {
      return;
    }

    const sliderEnabled = this.isSliderEnabledForViewport();
    const showProgress = this.root?.dataset.showProgress === 'true';
    const hasMultipleSlides = this.getSlideCount() > 1;

    const progressWrapper = this.progressBar.closest('[data-embla-progress]');
    if (progressWrapper) {
      progressWrapper.hidden = !showProgress || !sliderEnabled || !hasMultipleSlides;
    }

    if (!this.embla || !sliderEnabled || !hasMultipleSlides || !showProgress) {
      this.progressBar.style.width = '0%';
      return;
    }

    const progress = this.embla.scrollProgress();
    const boundedProgress = Number.isFinite(progress) ? Math.min(Math.max(progress, 0), 1) : 0;

    this.progressBar.style.width = `${boundedProgress * 100}%`;
  }

  resumeAutoplay() {
    if (!this.embla || !this.getAutoplayEnabled()) {
      return;
    }

    if (!this.isSliderEnabledForViewport() || this.getSlideCount() <= 1) {
      return;
    }

    this.pauseAutoplay();

    const intervalMs = this.getAutoplayIntervalMs();
    this.autoplayTimer = window.setInterval(() => {
      if (!this.embla) return;

      if (this.embla.canScrollNext()) {
        this.embla.scrollNext();
        return;
      }

      this.embla.scrollTo(0);
    }, intervalMs);
  }

  pauseAutoplay() {
    if (this.autoplayTimer) {
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  setLoadMore () {
    const cols = parseInt(getComputedStyle(this.root).getPropertyValue('--embla-grid-columns').trim(), 10);
    const rows = parseInt(getComputedStyle(this.root).getPropertyValue('--embla-grid-rows').trim(), 10);
    this.loadedCounts = rows*cols;
    this.slides.forEach((slide, index) => {
      if (index >= this.loadedCounts) {
        slide.classList.add('loaded');
      }
    });
  }

  onLoadMore () {
    this.loadedCounts += 3;
    this.slides.forEach((slide, index) => {
      if (index < this.loadedCounts) {
        slide.classList.remove('loaded');
      }
    });
  }

  setScrollSliding () {
  //   this.viewport?.addEventListener('wheel', (e) => {
      
  //     if (!this.embla || !this.root?.dataset.scrollSliding) return

  //     const isScrollingDown = e.deltaY > 0
  //     const canScrollNext = this.embla.canScrollNext()
  //     const canScrollPrev = this.embla.canScrollPrev()

  //     if ((isScrollingDown && canScrollNext) || (!isScrollingDown && canScrollPrev)) {
  //       e.preventDefault()

  //       if (isScrollingDown) {
  //         this.embla.scrollNext()
  //       } else {
  //         this.embla.scrollPrev()
  //       }
  //     }
  //   }, { passive: false })
  // }

        this.viewport?.addEventListener(
          'wheel',
          (e) => {
            if (!this.embla || !this.root?.dataset.scrollSliding) return
            if (this.isWheelScrolling) return

            const absX = Math.abs(e.deltaX)
            const absY = Math.abs(e.deltaY)
            const primaryDelta = absX > absY ? e.deltaX : e.deltaY


            if (Math.abs(primaryDelta) < 10 || absX <= absY) return

            const isNext = primaryDelta > 0
            const canScrollNext = this.embla.canScrollNext()
            const canScrollPrev = this.embla.canScrollPrev()

            if ((isNext && canScrollNext) || (!isNext && canScrollPrev)) {
              e.preventDefault()
              this.isWheelScrolling = true

              if (isNext) {
                this.embla.scrollNext()
              } else {
                this.embla.scrollPrev()
              }

              setTimeout(() => {
                this.isWheelScrolling = false
              }, 300)
            }
          },
          { passive: false }
        )
}



}

if (!customElements.get('embla-slider-component')) {
  customElements.define('embla-slider-component', EmblaSliderComponent);
}
